import React, { useState, useEffect, useRef } from 'react';
import { 
    Send, FileText, Paperclip, ArrowLeft, Mic, Trash2, 
    StopCircle, Play, Pause, Video, Image as ImageIcon,
    Loader2, Check
} from 'lucide-react';

import api from '../services/api'; 
import { socket } from '../services/socket';

// 🚨 IP FIXO DA VPS (IMPORTANTE PARA MOBILE)
const MEDIA_BASE_URL = 'https://wadesk.com.br';

// Utilitário de Tempo
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const InternalChatScreen = ({ users, currentUser }) => {
    // --- ESTADOS ---
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    // --- ÁUDIO ---
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordedAudio, setRecordedAudio] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingIntervalRef = useRef(null);

    // --- REFS ---
    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // =================================================================================
    // 1. CARREGAMENTO DE MENSAGENS (API + SOCKET)
    // =================================================================================
    const markAsRead = async (targetId) => {
        try {
            await api.put(`/internal-chats/${targetId}/read`);
            window.dispatchEvent(new Event('refresh_dashboard')); // Força MainLayout a buscar o novo contagem do emblema
        } catch (error) {
            console.error("Erro ao marcar como lido:", error);
        }
    };

    useEffect(() => {
        if (!selectedUser) return;

        // Carrega histórico inicial
        const loadMessages = async () => {
            try {
                // 🚨 CORREÇÃO: Usando _id do MongoDB
                const targetId = selectedUser._id || selectedUser.id;
                const res = await api.get(`/internal-chats/${targetId}`);
                setMessages(Array.isArray(res.data) ? res.data : []);
                
                // Marca como lido limpar o emblema "fantasma" do chat interno
                await markAsRead(targetId);
            } catch (error) {
                console.error("Erro ao carregar chat interno:", error);
            }
        };
        loadMessages();

    }, [selectedUser]);

    useEffect(() => {
        // Escuta novas mensagens em tempo real via Socket
        const handleNewMessage = (data) => {
            if (!selectedUser) return;

            const targetId = selectedUser._id || selectedUser.id;
            const myId = currentUser._id || currentUser.id;

            // Verifica se a mensagem pertence a conversa atual
            const isRelevant = 
                (data.senderId === targetId && data.targetId === myId) ||
                (data.senderId === myId && data.targetId === targetId);

            if (isRelevant) {
                setMessages(prev => [...prev, data.message]);
                // Scroll suave após receber mensagem
                setTimeout(() => {
                    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }, 100);

                // Marca imediatamente como lido se a janela do chat estiver aberta
                if (data.senderId === targetId) {
                    markAsRead(targetId);
                }
            }
        };

        socket.on('internal_message', handleNewMessage);

        return () => {
            socket.off('internal_message', handleNewMessage);
        };
    }, [selectedUser, currentUser]);

    // Scroll para o fim ao mudar chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, recordedAudio]);

    // =================================================================================
    // 2. LÓGICA DE ÁUDIO
    // =================================================================================
    const startRecording = async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert('Erro: O navegador bloqueou o acesso ao microfone porque o sistema não está rodando em um ambiente seguro (HTTPS/localhost).');
                return;
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioUrl = URL.createObjectURL(audioBlob);
                setRecordedAudio({ blob: audioBlob, url: audioUrl });
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);
            
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (error) {
            console.error("Erro ao iniciar gravação:", error);
            alert("Não foi possível acessar o microfone.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(recordingIntervalRef.current);
        }
    };

    const cancelRecording = () => {
        stopRecording();
        setRecordedAudio(null);
        setRecordingTime(0);
    };

    // =================================================================================
    // 3. ENVIO DE MENSAGENS (TEXTO E MÍDIA)
    // =================================================================================
    
    const handleSendText = async (e) => {
        e?.preventDefault();
        if (!newMessage.trim() || !selectedUser) return;

        const textToSend = newMessage.trim();
        setNewMessage(''); 

        try {
            setIsSending(true);
            // 🚨 CORREÇÃO: Usando _id
            const targetId = selectedUser._id || selectedUser.id;
            await api.post('/internal-chats/message', {
                targetId: targetId,
                body: textToSend,
                type: 'text'
            });
        } catch (error) {
            alert("Erro ao enviar mensagem.");
            setNewMessage(textToSend); 
        } finally {
            setIsSending(false);
        }
    };

    const handleSendAudio = async () => {
        if (!recordedAudio || !selectedUser) return;

        const formData = new FormData();
        const targetId = selectedUser._id || selectedUser.id; // 🚨 CORREÇÃO

        formData.append('file', recordedAudio.blob, 'audio.webm');
        formData.append('targetId', targetId);

        try {
            setIsSending(true);
            await api.post('/internal-chats/media', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setRecordedAudio(null);
            setRecordingTime(0);
        } catch (error) {
            alert("Erro ao enviar áudio.");
        } finally {
            setIsSending(false);
        }
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file || !selectedUser) return;

        const formData = new FormData();
        const targetId = selectedUser._id || selectedUser.id; // 🚨 CORREÇÃO

        formData.append('file', file);
        formData.append('targetId', targetId);

        try {
            setIsSending(true);
            await api.post('/internal-chats/media', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } catch (error) {
            alert("Erro ao enviar arquivo.");
        } finally {
            setIsSending(false);
            event.target.value = null; 
        }
    };

    // =================================================================================
    // 4. RENDERIZAÇÃO
    // =================================================================================
    
    const MessageBubble = ({ msg }) => {
        const myId = currentUser._id || currentUser.id;
        const isMe = msg.senderId === myId;
        
        const mediaSrc = msg.mediaUrl ? (msg.mediaUrl.startsWith('http') ? msg.mediaUrl : `${MEDIA_BASE_URL}${msg.mediaUrl}`) : null;

        return (
            <div className={`flex w-full flex-col ${isMe ? 'items-end' : 'items-start'} mb-3`}>
                <div className={`max-w-[85%] lg:max-w-md px-3 py-2 rounded-lg shadow-sm ${isMe ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'}`}>
                    
                    {msg.type === 'text' && <p className="whitespace-pre-wrap break-words text-sm">{msg.body}</p>}
                    
                    {msg.type === 'image' && mediaSrc && (
                        <img src={mediaSrc} alt="imagem" className="rounded-lg max-w-full cursor-pointer hover:opacity-90" onClick={() => window.open(mediaSrc)} />
                    )}
                    
                    {msg.type === 'video' && mediaSrc && (
                        <video controls src={mediaSrc} className="rounded-lg max-w-full max-h-60" />
                    )}
                    
                    {msg.type === 'audio' && mediaSrc && (
                        <audio controls src={mediaSrc} className="max-w-[240px] h-10 mt-1" />
                    )}
                    
                    {msg.type === 'document' && mediaSrc && (
                        <a href={mediaSrc} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded ${isMe ? 'bg-blue-700' : 'bg-gray-100'} hover:opacity-80 transition-opacity`}>
                            <FileText className="w-5 h-5" />
                            <span className="truncate text-sm underline">{msg.fileName || 'Arquivo'}</span>
                        </a>
                    )}

                    <div className={`text-[10px] mt-1 text-right flex items-center justify-end gap-1 ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                        <span>
                            {msg.timestamp 
                                ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                                : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            }
                        </span>
                        {isMe && <Check className="w-3 h-3" />}
                    </div>
                </div>
            </div>
        );
    };

    // 🚨 FILTRAGEM CORRIGIDA PARA EXIBIR LISTA
    // Filtra o próprio usuário da lista
    const filteredUsers = users.filter(u => {
        const uId = u._id || u.id;
        const myId = currentUser._id || currentUser.id;
        return uId !== myId;
    });

    return (
        <div className="flex h-full bg-white overflow-hidden">
            
            {/* --- LISTA DE USUÁRIOS (SIDEBAR) --- */}
            <div className={`border-r border-gray-200 flex-col bg-white ${selectedUser ? 'hidden md:flex' : 'flex w-full'} md:w-80 lg:w-96`}>
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-800">Equipe</h2>
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                        {filteredUsers.length}
                    </span>
                </div>
                <div className="overflow-y-auto flex-1 custom-scrollbar">
                    {filteredUsers.map(user => {
                        const userId = user._id || user.id; // Garante ID
                        return (
                            <div 
                                key={userId} 
                                onClick={() => setSelectedUser(user)} 
                                className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-all flex items-center gap-3 ${
                                    (selectedUser?._id || selectedUser?.id) === userId 
                                    ? 'bg-blue-50 border-l-4 border-l-blue-600' 
                                    : 'border-l-4 border-transparent'
                                }`}
                            >
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center font-bold text-gray-600">
                                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-800 truncate">{user.name}</p>
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${user.online ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                                        <p className="text-xs text-gray-500">{user.role === 'admin' ? 'Administrador' : (user.is_supervisor ? 'Gestor' : 'Atendente')}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    
                    {filteredUsers.length === 0 && (
                        <div className="p-8 text-center text-gray-400 text-sm">
                            Nenhum outro membro da equipe encontrado.
                        </div>
                    )}
                </div>
            </div>

            {/* --- ÁREA DE CHAT --- */}
            <div className={`flex-col bg-[#e5ddd5] ${selectedUser ? 'flex w-full' : 'hidden md:flex'} md:flex-1 relative`}>
                {selectedUser ? (
                    <>
                        {/* HEADER */}
                        <div className="p-3 border-b bg-white flex items-center shadow-sm z-10">
                            <button onClick={() => setSelectedUser(null)} className="mr-3 p-2 rounded-full text-gray-600 hover:bg-gray-100 md:hidden">
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600 mr-3">
                                {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div>
                                <h3 className="font-bold text-lg text-gray-800 leading-tight">{selectedUser.name}</h3>
                                <p className="text-xs text-green-600 font-semibold">Chat Interno</p>
                            </div>
                        </div>

                        {/* MENSAGENS */}
                        <div className="flex-1 p-4 overflow-y-auto custom-scrollbar bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-fixed">
                            {messages.map((msg, index) => <MessageBubble key={index} msg={msg} />)}
                            <div ref={chatEndRef} />
                        </div>

                        {/* ÁREA DE INPUT */}
                        <div className="p-2 bg-white border-t">
                            {isRecording ? (
                                <div className="flex items-center justify-between bg-red-50 p-3 rounded-lg animate-pulse">
                                    <div className="flex items-center gap-2 text-red-600 font-bold">
                                        <div className="w-3 h-3 bg-red-600 rounded-full animate-ping"></div>
                                        Gravando: {formatTime(recordingTime)}
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={cancelRecording} className="p-2 text-gray-500 hover:bg-red-200 rounded-full"><Trash2 className="w-5 h-5"/></button>
                                        <button onClick={stopRecording} className="p-2 text-red-600 hover:bg-red-200 rounded-full"><StopCircle className="w-6 h-6"/></button>
                                    </div>
                                </div>
                            ) : recordedAudio ? (
                                <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-lg">
                                    <audio controls src={recordedAudio.url} className="flex-1 h-8" />
                                    <button onClick={() => setRecordedAudio(null)} className="p-2 text-red-500 hover:bg-red-100 rounded-full"><Trash2 className="w-5 h-5"/></button>
                                    <button onClick={handleSendAudio} className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 shadow-sm"><Send className="w-5 h-5"/></button>
                                </div>
                            ) : (
                                <form onSubmit={handleSendText} className="flex items-center gap-2">
                                    <button type="button" onClick={() => fileInputRef.current.click()} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors">
                                        <Paperclip className="h-6 w-6" />
                                    </button>
                                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" />

                                    <div className="flex-1 relative">
                                        <input 
                                            type="text" 
                                            value={newMessage} 
                                            onChange={(e) => setNewMessage(e.target.value)} 
                                            placeholder="Digite sua mensagem..." 
                                            className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 pr-10" 
                                        />
                                    </div>

                                    {newMessage.trim() ? (
                                        <button type="submit" disabled={isSending} className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-md transition-transform active:scale-95 disabled:bg-gray-400">
                                            {isSending ? <Loader2 className="w-5 h-5 animate-spin"/> : <Send className="h-5 w-5" />}
                                        </button>
                                    ) : (
                                        <button type="button" onClick={startRecording} className="bg-gray-200 text-gray-600 p-3 rounded-full hover:bg-gray-300 shadow-sm">
                                            <Mic className="h-5 w-5" />
                                        </button>
                                    )}
                                </form>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6 text-center">
                        <div className="bg-gray-200 p-6 rounded-full mb-4">
                            <Send className="w-12 h-12 text-gray-400" />
                        </div>
                        <h2 className="font-bold text-xl text-gray-600">Chat Interno</h2>
                        <p className="text-gray-500 mt-2 max-w-xs">Selecione um membro da equipe para iniciar uma conversa privada.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InternalChatScreen;